/* ============================================================
   EntitlementContext — the app's single answer to "what may this account do?".

   Sits ABOVE the store in the tree, because the gate it feeds decides whether
   the store's screens mount at all. Reads three sources, in priority order:

     1. **The server**, via `plan` on `GET /v1/sync`. Authoritative
        (docs/subscriptions.md §7) — the client never decides its own
        entitlement.
     2. **The disk cache**, for a cold launch with no signal. Trusted for
        `OFFLINE_GRACE_DAYS` since the last successful sync (§5).
     3. **An optimistic hint** set by the paywall the moment a purchase
        succeeds, so the wall dismisses immediately instead of waiting for a
        RevenueCat webhook to land and a sync to notice. Cleared by the next
        real server value.
     4. **The store SDK itself** (RevenueCat on mobile — `adapters.billing`;
        absent on a platform with no store SDK), asked by this device, as the backstop for a lost
        webhook (docs/subscriptions.md §7b). The server learns about purchases
        from a webhook; webhooks get lost, and the failure is a paying customer
        walled out forever, because nothing re-asks. So whenever the server says
        unentitled, the device asks the store directly — and if the store
        disagrees, it grants IMMEDIATELY and then asks the server to go and
        verify with RevenueCat for itself.

   Source 4 only ever GRANTS. It cannot revoke, which is what keeps "the client
   never decides its own entitlement" true: the server still owns the value, and
   a tampered client can only ask us to look again.

   ⚠️ The rule that governs every ambiguous case: **an entitlement that cannot
   be determined resolves to GRANTED, never denied.** An app that locks someone
   out of their own habits because a billing server was unreachable contradicts
   the offline-first promise in CLAUDE.md, and it is a one-star review with a
   screenshot attached. The accepted cost is that staying offline buys free Pro;
   §5 makes that trade deliberately.
   ============================================================ */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
  cacheWithinGrace,
  canRead,
  canWrite,
  isEntitled,
  isPlan,
  isPlanReason,
  isPlanStore,
  needsHardPaywall,
  type Plan,
  type PlanReason,
  type PlanStore,
} from '@habishine/shared';

import { useApi } from '../api/useApi';
import { useAuth } from '../auth/AuthContext';
import { useClientCore, usePersist } from '../provider';

interface EntitlementValue {
  /** The resolved plan. `null` only while the cache is still being read. */
  plan: Plan | null;
  /** Why, when the app words the plan differently — `transferred` when this
      account's subscription moved to another account on the same Apple ID
      (docs/subscriptions.md §7c). Null whenever the account is entitled. */
  planReason: PlanReason | null;
  /** Which store bills the plan — `app_store` or `web` — so Settings can point
      "manage subscription" at the iOS sheet or at the web customer portal. Null
      when unknown; a screen then falls back to its own platform's default. */
  planStore: PlanStore | null;
  /** Still reading the disk cache — render nothing rather than flashing a
      paywall at someone who turns out to be a subscriber. */
  loading: boolean;
  /** May create, edit, complete, check in. False for `expired`, which keeps
      READ access (§6) — their data stays visible and deletion stays reachable. */
  canWrite: boolean;
  /** May see the app at all. False only for `none`. */
  canRead: boolean;
  /** Show the paywall as an unavoidable gate rather than a banner. */
  hardPaywall: boolean;
  /** Called by the sync layer with the server's value. */
  adopt: (plan: Plan, reason?: PlanReason | null, store?: PlanStore | null) => void;
  /** Called by the paywall on a successful purchase or restore. Optimistic —
      the next server value overrides it. Also nudges the server to reconcile,
      so its copy catches up without waiting on the webhook. `store` is which
      checkout just succeeded, so Settings points at the right place before
      the server has said so. */
  grantOptimistically: (store?: PlanStore) => void;
  /** True while a blocked write is asking for the paywall to be shown. */
  upgradeRequested: boolean;
  /** Raised by the write guard when a lapsed account attempts a write (§6).
      Distinct from `hardPaywall`: that one REPLACES the app for a `none`
      account, while this one is a modal over an app the user can still read. */
  requestUpgrade: () => void;
  dismissUpgrade: () => void;
}

const Ctx = createContext<EntitlementValue | null>(null);

export function EntitlementProvider({ children }: { children: React.ReactNode }) {
  const { user, status } = useAuth();
  const api = useApi();
  const disk = usePersist();
  const { billing } = useClientCore().adapters;
  const userKey = user?.sub ?? null;

  const [plan, setPlan] = useState<Plan | null>(null);
  const [planReason, setPlanReason] = useState<PlanReason | null>(null);
  const [planStore, setPlanStore] = useState<PlanStore | null>(null);
  const [loading, setLoading] = useState(true);
  const [upgradeRequested, setUpgradeRequested] = useState(false);
  // Set by a purchase, cleared by the next server value. A ref rather than
  // state for the "has the server spoken since" flag: it must not re-render.
  const serverSpoke = useRef(false);

  /* Cold start: read the cache before the first render that could show a wall. */
  useEffect(() => {
    let alive = true;
    if (status === 'loading') return;
    if (!userKey) {
      setPlan(null);
      setPlanReason(null);
      setPlanStore(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    serverSpoke.current = false;
    void disk.loadPlanCache(userKey).then((cache) => {
      if (!alive) return;
      if (!cache) {
        // NEVER synced on this device. We genuinely do not know, so grant —
        // a fresh install that cannot reach the network gets in, and the first
        // successful sync corrects it. Walling here would punish a user for a
        // failure that is not theirs.
        setPlan('pro');
        setPlanReason(null);
        setPlanStore(null);
      } else if (cacheWithinGrace(cache.confirmedAt)) {
        setPlan(cache.plan);
        setPlanReason(cache.plan === 'expired' ? (cache.reason ?? null) : null);
        setPlanStore(cache.store ?? null);
      } else {
        // Stale beyond the grace window. This is not "cannot determine" — it is
        // old information, and honouring it forever is how staying offline
        // becomes a permanent free tier.
        setPlan(cache.plan === 'none' ? 'none' : 'expired');
        setPlanReason(cache.plan === 'expired' ? (cache.reason ?? null) : null);
        setPlanStore(cache.store ?? null);
      }
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [userKey, status, disk]);

  /** The server's value wins over everything, and re-stamps the cache's age. */
  // A ref so the reconciliation callbacks can adopt a server value without
  // being re-created every time `adopt` is, and without depending on the order
  // the callbacks are declared in.
  const adoptRef = useRef<(p: Plan, reason?: PlanReason | null, store?: PlanStore | null) => void>(() => {});

  const adopt = useCallback(
    (next: Plan, reason: PlanReason | null = null, store: PlanStore | null = null) => {
      serverSpoke.current = true;
      setPlan(next);
      setPlanReason(reason);
      setPlanStore(store);
      if (userKey) {
        void disk.savePlanCache(userKey, { plan: next, confirmedAt: new Date().toISOString(), reason, store });
      }
    },
    [userKey, disk],
  );
  adoptRef.current = adopt;

  /** Ask the server to re-check with RevenueCat and adopt whatever it says.

      The request carries NO claim — the route reads the caller's `sub` from the
      verified token and asks RevenueCat with the secret key. So this is "please
      look", never "I am entitled", and the server's answer wins because its pull
      is fresher and less forgeable than the SDK's cached `CustomerInfo`. */
  const askServerToReconcile = useCallback(async () => {
    try {
      const res = await api.post<{ plan?: unknown; planReason?: unknown; planStore?: unknown }>('/v1/billing/reconcile');
      if (isPlan(res?.plan)) {
        adoptRef.current(
          res.plan,
          isPlanReason(res?.planReason) ? res.planReason : null,
          isPlanStore(res?.planStore) ? res.planStore : null,
        );
      }
    } catch {
      // Offline, or the server could not reach RevenueCat. The local grant
      // stands — §5's fail-open rule: never take away access over a failure
      // that is not the user's.
    }
  }, [api]);

  /** Optimistic unlock straight after a purchase.

      The webhook → `users.plan` → next sync round-trip takes seconds at best and
      can take longer; making someone stare at a paywall they have just paid to
      remove is the worst possible moment to be slow. Deliberately does NOT
      stamp `confirmedAt` as a server confirmation — it writes the cache so a
      force-quit right after paying still lands in the app, but the next sync
      replaces it with the truth. */
  const grantOptimistically = useCallback((store?: PlanStore) => {
    setPlan('pro');
    setPlanReason(null);
    if (store) setPlanStore(store);
    if (userKey) {
      void disk.savePlanCache(userKey, { plan: 'pro', confirmedAt: new Date().toISOString(), store: store ?? null });
    }
    // Nudge the server rather than waiting on the webhook. The purchase just
    // happened, so RevenueCat already knows — a pull right now is the fastest
    // route to the server agreeing, and covers the case where the webhook for
    // this very purchase is the one that gets lost.
    void askServerToReconcile();
  }, [userKey, askServerToReconcile, disk]);


  /* Backstop: the server says unentitled, so ask the store whether it agrees.

     Runs once per account per session, and only when there is something to
     repair — an entitled plan needs no second opinion, and asking on every
     render would be a vendor call per render. */
  const checkedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!userKey || !billing || plan === null || loading) return;
    if (isEntitled(plan)) return;
    if (checkedFor.current === userKey) return;
    checkedFor.current = userKey;

    let alive = true;
    void billing.storeSaysEntitled().then((yes) => {
      if (!alive || !yes) return;
      // Grant FIRST. Someone who has paid should not wait on a round-trip to
      // stop being locked out of what they paid for.
      setPlan('pro');
      setPlanReason(null);
      void askServerToReconcile();
    });
    return () => {
      alive = false;
    };
  }, [userKey, billing, plan, loading, askServerToReconcile]);

  /* The case a launch-time check misses: the entitlement changes while the app
     is already open — a renewal landing, or a restore on another device. */
  useEffect(() => {
    if (!userKey || !billing) return;
    return billing.onStoreEntitlementChange((nowEntitled) => {
      if (!nowEntitled) return; // grant-only, exactly as at launch
      setPlan('pro');
      setPlanReason(null);
      void askServerToReconcile();
    });
  }, [userKey, billing, askServerToReconcile]);

  const requestUpgrade = useCallback(() => setUpgradeRequested(true), []);
  const dismissUpgrade = useCallback(() => setUpgradeRequested(false), []);

  // Entitlement regained (purchase, restore, or a server value) closes the
  // modal on its own — leaving a paywall up over an app the user can now use
  // would be its own bug.
  useEffect(() => {
    if (plan !== null && canWrite(plan)) setUpgradeRequested(false);
  }, [plan]);

  const value = useMemo<EntitlementValue>(
    () => ({
      plan,
      planReason,
      planStore,
      loading,
      canWrite: plan === null ? true : canWrite(plan),
      canRead: plan === null ? true : canRead(plan),
      hardPaywall: plan === null ? false : needsHardPaywall(plan),
      adopt,
      grantOptimistically,
      upgradeRequested,
      requestUpgrade,
      dismissUpgrade,
    }),
    [plan, planReason, planStore, loading, adopt, grantOptimistically, upgradeRequested, requestUpgrade, dismissUpgrade],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useEntitlement(): EntitlementValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useEntitlement must be used inside EntitlementProvider');
  return v;
}

/** Convenience for the many call sites that only care whether a write is
    allowed. Kept as its own hook so a screen reads `const canEdit = useCanWrite()`
    rather than destructuring a context it otherwise ignores. */
export function useCanWrite(): boolean {
  return useEntitlement().canWrite;
}
